import { Dayjs } from 'dayjs';
import { QueryKey, useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { LocalEvent } from 'src/Hooks/Queries/useFetchEvents';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { eventsApi } from 'src/Services/API';
import { trackEvent } from 'src/Services/Matomo';

type Context = { previousQueries: Map<QueryKey, LocalEvent[]> } | undefined;

interface EventDeleteParams {
  eventId: string;
  occurrenceDate?: Dayjs;
}

export const useDeleteEvent = (
  options?: UseMutationOptions<void, unknown, EventDeleteParams, Context>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<void, unknown, EventDeleteParams, Context>(
    ['delete-event', assetId],
    async ({ eventId, occurrenceDate }: EventDeleteParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await eventsApi.eventsControllerRemove(assetId, eventId, occurrenceDate, {
        transformResponse: [],
      });
      return response.data;
    },
    {
      ...options,
      onMutate: async params => {
        options?.onMutate?.(params);
        await queryClient.cancelQueries(['fetch-events', assetId]);

        const allQueries = queryClient.getQueriesData<LocalEvent[]>(['fetch-events', assetId]);
        const previousQueries = new Map<QueryKey, LocalEvent[]>();

        allQueries.forEach(([queryKey, events]) => {
          previousQueries.set(queryKey, events);

          queryClient.setQueryData<LocalEvent[]>(queryKey, old => {
            if (!old) return [];

            // CASE 1: Deleting a single occurrence
            if (params.occurrenceDate) {
              // Converts the occurrence date to an ISO string for reliable comparison,
              // because event.startTime in the cache is also an ISO string.
              const occurrenceIso = params.occurrenceDate.toISOString();
              return old.filter(
                event =>
                  !(event.id === params.eventId && event.startTime.toISOString() === occurrenceIso)
              );
            }

            // CASE 2: Deleting the entire series (previous behavior)
            else {
              return old.filter(event => event.id !== params.eventId);
            }
          });
        });

        return { previousQueries };
      },
      onError: (_error, _variables, context?: Context) => {
        // restore previous state
        context?.previousQueries.forEach((events, queryKey) => {
          queryClient.setQueryData(queryKey, events);
        });
      },
      onSettled: () => {
        // invalidate all fetch events queries
        queryClient.invalidateQueries(['fetch-events', assetId]);
        trackEvent('Planning', 'delete');
      },
    }
  );
};
